import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, forkJoin, map, of } from 'rxjs';

/**
 * Lets a lazy feature's routes activate only once its translation scope (and the root file) is
 * in memory for the active language.
 *
 * Without it the scope is fetched the first time a `*transloco` directive renders, which on a
 * direct page load can be after the page has already built labels in code: a synchronous
 * `translate()` then hands back the key itself, and anything that caches the result keeps it.
 * The files are small and cached by Transloco, so this costs one request per scope per session.
 */
export function translationScopeGuard(scope: string): CanActivateFn {
  return () => {
    const translocoService = inject(TranslocoService);
    const lang = translocoService.getActiveLang();
    // Transloco addresses a scope's file as `<scope>/<lang>`; a bare scope name would be read as a language.
    return forkJoin([translocoService.load(lang), translocoService.load(`${scope}/${lang}`)]).pipe(
      map(() => true),
      // A missing file is logged by the loader; the page still opens and falls back to the keys.
      catchError(() => of(true)),
    );
  };
}
